/**
 * mip_bias — a texture LOD bias that follows the render scale (2026-09-25,
 * visual-realism R3.4).
 *
 * WHY. The scene renders at 1/ratio of the output (FSR 1.3–2×, the governor
 * moves it live) and every texture is sampled at the RENDER footprint, i.e. a
 * mip too blurry for the pixels the upscaler rebuilds — AMD's FSR 1 and 2 guides
 * both require a negative bias (`log2(render / display)`, one more −1 under a
 * temporal upscaler whose jitter resolves the extra detail). A WebGPU sampler
 * has NO lod-bias field (GPUSamplerDescriptor), so the bias must be in the
 * shader: `textureSampleBias`.
 *
 * HOW. Asset textures are FLAGGED at parse (`flagMipBias`: `userData.mipBias`
 * on the colour / normal / ORM / emissive slots of a loaded GLB), and one
 * patch on `TextureNode.setup` hands a flagged texture's FRAGMENT sample the
 * shared `uMipBias` uniform — so every slot of every material path gets it
 * (MaterialNode's own map reads, the snow/grime/wetness layers that re-read
 * `materialColor`, the ORM alpha read) without rewriting a single graph. The
 * vertex stage never gets a bias (WGSL has no textureSampleBias there), nor
 * does a sample that already chose its level / gradient / bias.
 *
 * THE CURVE (`mipBiasFor`): spatial FSR → max(log2(1/scale), SPATIAL_CAP)
 * (−0.38 at 1.3×, −0.5 cap at 1.5× and wider — without a temporal resolve a
 * deeper bias is shimmer, the research's cap on SMAA tiers); TAAU →
 * log2(1/scale) − 0.5 (−0.88 at 1.3×, −1.08 at 1.5×); native → 0 (−0.5 under TAA).
 */
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';

export const uMipBias = uniform(0);
export const SPATIAL_CAP = -0.5;
export const TEMPORAL_EXTRA = -0.5;
/** The slots whose textures take the bias (sampled in the fragment stage, from UVs). */
export const MIP_BIAS_SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'specularIntensityMap', 'clearcoatNormalMap'] as const;

export const mipBiasStats = { flagged: 0, injected: 0, installed: false, value: 0 };

/** The bias for a render scale (output ÷ render, ≥ 1) under an upscale mode. */
export function mipBiasFor(renderScale: number, mode: 'fsr' | 'taau' | 'off' | 'traa'): number {
  const s = Math.max(1, Number.isFinite(renderScale) ? renderScale : 1);
  const base = s > 1.0001 ? Math.log2(1 / s) : 0;
  if (mode === 'taau' || mode === 'traa') return base + TEMPORAL_EXTRA;
  return Math.max(SPATIAL_CAP, base);
}

/** Flag a parsed scene's textures (idempotent). */
export function flagMipBias(root: THREE.Object3D): number {
  let n = 0;
  root.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
    for (const mat of Array.isArray(m) ? m : m ? [m] : []) {
      for (const k of MIP_BIAS_SLOTS) {
        const t = (mat as unknown as Record<string, THREE.Texture | null | undefined>)[k];
        if (t && t.isTexture && t.userData.mipBias !== true) { t.userData.mipBias = true; n++; }
      }
    }
  });
  mipBiasStats.flagged += n;
  return n;
}

/** Patch `TextureNode.setup` once so a flagged texture's fragment sample carries `uMipBias`. */
export function installMipBias(): boolean {
  if (mipBiasStats.installed) return true;
  const TN = (THREE as unknown as { TextureNode?: { prototype: Record<string, unknown> } }).TextureNode;
  const proto = TN?.prototype as { setup?: (b: unknown) => unknown } | undefined;
  if (!proto?.setup) return false;
  const orig = proto.setup;
  proto.setup = function patched(this: { biasNode: unknown; levelNode: unknown; gradNode?: unknown; compareNode?: unknown; value?: { userData?: Record<string, unknown> } }, builder: unknown): unknown {
    const b = builder as { shaderStage?: string };
    const inject = this.biasNode === null && this.levelNode === null && (this.gradNode ?? null) === null && (this.compareNode ?? null) === null
      && this.value?.userData?.mipBias === true && b.shaderStage === 'fragment';
    if (!inject) return orig.call(this, builder);
    // Kept on the node (not restored): the analyze stage and the program cache key walk the node's own
    // members, so a bias that existed only inside setup() would be invisible to both.
    this.biasNode = uMipBias;
    mipBiasStats.injected++;
    return orig.call(this, builder);
  };
  mipBiasStats.installed = true;
  return true;
}

/** Drive the uniform (every frame, cheap). `enabled` false pins 0. */
export function setMipBias(v: number, enabled: boolean): void {
  const b = enabled ? v : 0;
  (uMipBias as unknown as { value: number }).value = b;
  mipBiasStats.value = b;
}
